import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime, ExportDropdown, Hours, RouteLink } from '~/components';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig, useTimeEntriesReportURL } from '~/hooks';
import useMemberFilters from '~/hooks/useMemberFilters';
import { PageLoader } from '~/routes/public/pages';
import { QuerySort, dateFormats, intervalOptions } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

export default function Timesheets({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const searchParamsConfig = useSearchParamsConfig();

  const memberFilters = useMemberFilters(() => ({
    memberStatusId: { ...searchParamsConfig.memberStatusId, default: 'active' },
  }));

  const [params, setParams] = useState({
    period: null,
    sort: new QuerySort('member.name', 'asc'),
    ...memberFilters.filters,
  });

  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: { ...reportsSearchParamsConfig.period, default: intervalOptions.last_week },
        sort: { ...searchParamsConfig.sort, default: new QuerySort('member.name', 'asc') },
        ...memberFilters.searchParamsConfig,
      }),
      [reportsSearchParamsConfig, searchParamsConfig, memberFilters],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      ...memberFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter
          clearable={false}
          value={params.period}
          onChange={({ value }) => handleApplyFilters({ period: value })}
        />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const timeDetail = useTimeEntriesReportURL({ params });

  const groups = useMemo(() => {
    return _.groupBy(report.records, (record) => `${record.start}_${record.end}`);
  }, [report.records]);

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky width="14rem" name="timesheet.start" onSort={onSort} sort={params.sort}>
            Period
          </Table.Column>
          <Table.Column width="12rem">Submitted On</Table.Column>
          <Table.Column minWidth="14rem" name="member.name" onSort={onSort} sort={params.sort}>
            Member
          </Table.Column>
          <Table.Column width="10rem">Member Type</Table.Column>
          <Table.Column width="10rem" align="right">
            Hours
          </Table.Column>
          <Table.Column width="10rem" align="right">
            Capacity
          </Table.Column>
        </Table.Header>

        <Table.Body fade={query.status === 'sorting'}>
          {_.map(groups, (timesheets, periodKey) => {
            const period = timesheets[0];

            const hours = _.sumBy(timesheets, 'hours');
            const capacity = _.sumBy(timesheets, 'capacity');

            return (
              <React.Fragment key={periodKey}>
                <Table.GroupRow top="3rem">
                  <Table.Cell>
                    <strong>
                      <DateTime value={period.start} /> - <DateTime value={period.end} />
                    </strong>
                  </Table.Cell>
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell>
                    <strong>
                      <Hours value={hours} />
                    </strong>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>
                      <Hours value={capacity} />
                    </strong>
                  </Table.Cell>
                </Table.GroupRow>

                {timesheets.map((timesheet) => {
                  return (
                    <Table.Row key={timesheet.id}>
                      <Table.Cell />
                      <Table.Cell>
                        <DateTime value={timesheet.createdAt} format={dateFormats.compactDateTime} />
                      </Table.Cell>
                      <Table.Cell>{timesheet.member.name}</Table.Cell>
                      <Table.Cell>{timesheet.member.employmentType?.name}</Table.Cell>
                      <Table.Cell>
                        <RouteLink
                          to={timeDetail({
                            memberId: timesheet.member.id,
                            start: timesheet.start,
                            end: timesheet.end,
                          })}>
                          <Hours value={timesheet.hours} />
                        </RouteLink>
                      </Table.Cell>
                      <Table.Cell>
                        <Hours value={timesheet.capacity} />
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Table.Body>

        <Table.Status
          total={report.records.length}
          label="Timesheet"
          isLoading={query.status !== 'ready'}
          partialResults={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.period}
        clearable={false}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
